import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ButtonLink } from "@/components/ui/buttonlink";
import Link from "@/components/ui/link";
import { Skeleton } from "@/components/ui/skeleton";
import AccountLinks from "@/features/user/components/account-links";
import { useLng } from "@/hooks/use-lng";
import { useTheme } from "@/hooks/use-theme";
import { appTitleHead } from "@/lib/app-document-title";
import { useTranslation } from "@/lib/i18n/client";
import { useAuthStore } from "@/store/use-auth-store";
import useShowToast from "@/utils/useShowToast";
import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "oidc-react";
import { useShallow } from "zustand/react/shallow";

export const Route = createFileRoute("/_app/account/")({
  head: () => appTitleHead("desktop-nav-account"),
  component: AccountPage
});

function AccountPage() {
  const { signIn, userData, signOut, signOutRedirect } = useAuth();
  const lng = useLng();
  const { t } = useTranslation(lng, "translation");
  const { theme } = useTheme();
  const showToast = useShowToast();
  const [whoami, profileImage, setWhoami, setProfileImage] = useAuthStore(
    useShallow((state) => [
      state.whoami,
      state.profileImage,
      state.setWhoami,
      state.setProfileImage
    ])
  );

  if (!userData) {
    return (
      <div>
        <h2>{t("account-logged-out-header")}</h2>
        <p>{t("account-logged-out-description")}</p>
        <Button className="mt-6 w-full md:mr-4 md:w-fit" onClick={() => signIn()}>
          {t("login")}
        </Button>
        <ButtonLink
          variant={theme === "dark" ? "secondary" : "outline"}
          className="mt-4 w-full md:mr-4 md:w-fit"
          to="/account/register"
        >
          {t("create-account")}
        </ButtonLink>
        <ButtonLink
          variant={theme === "dark" ? "secondary" : "outline"}
          className="mt-4 w-full md:w-fit"
          to="/requestpassword"
        >
          {t("forgot-password")}
        </ButtonLink>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-6 hidden text-left md:block">{t("desktop-nav-account")}</h1>
      <Link
        to="/account/customerinfo"
        className="text-foreground focus-visible:ring-ring mb-5 inline-flex items-center gap-4 rounded-md no-underline transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <Avatar className="h-16 w-16 md:h-32 md:w-32">
          <AvatarImage
            className="object-cover"
            src={profileImage}
            alt={`${t("photo-alt")} ${whoami?.name}`}
          />
          <AvatarFallback>
            <Skeleton className="h-40 w-40 rounded-full" />
          </AvatarFallback>
        </Avatar>
        <span className="text-xl font-semibold">{whoami?.name}</span>
      </Link>
      <div className="flex flex-col items-center gap-3 md:flex-row">
        <Button
          variant="destructive"
          className="w-full md:w-fit"
          onClick={() => {
            const idTokenHint = userData?.id_token;
            setWhoami(null);
            setProfileImage("");
            void (async () => {
              await signOut();
              await signOutRedirect(idTokenHint ? { id_token_hint: idTokenHint } : undefined);
            })();
            showToast(200, "logged out successfully");
          }}
        >
          {t("logout")}
        </Button>
      </div>
      <div
        className="mt-6 flex flex-col gap-4 md:grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(295px, 1fr))" }}
      >
        <AccountLinks />
      </div>
    </div>
  );
}
